/**
 * Jansen's linkage.
 *
 * Eleven bars, one crank and one fixed offset. Theo Jansen searched for the set of
 * lengths that makes the foot draw a long flat stroke and then pick itself up over
 * the return — the magic numbers below. Nothing about them is derivable; they are
 * the output of a genetic search, which is exactly why the toy is worth making. You
 * can move any one of them and watch a walking machine turn into a twitch.
 *
 * The whole mechanism is one crank position plus five circle–circle intersections,
 * in a fixed order. Each joint is fully determined by two bars whose other ends are
 * already known, so there is no solver and no iteration here: it unrolls.
 *
 *   A ── fixed pivot on the frame, the origin of a leg
 *   B ── crank axle, at (a, l) from A. `a` and `l` are the frame, not bars.
 *   P ── crank pin, the only thing that moves under its own power
 *   T ── top joint      (b from A, j from P)
 *   L ── back joint     (d from A, e from T)
 *   M ── lower joint    (c from A, k from P)
 *   K ── knee           (f from L, g from M)
 *   F ── foot           (i from M, h from K)
 *
 * Coordinates are y-up and in Jansen's own units, so the numbers on screen are the
 * numbers in the table. A leg at rest reaches about 84 units below its pivot.
 */

/** Theo Jansen's numbers. Every length on the page is measured against these. */
export const MAGIC = {
  a: 38.0,
  b: 41.5,
  c: 39.3,
  d: 40.1,
  e: 55.8,
  f: 39.4,
  g: 36.7,
  h: 65.7,
  i: 49.0,
  j: 50.0,
  k: 61.9,
  l: 7.8,
  m: 15.0,
};

export const KEYS = Object.keys(MAGIC);

/**
 * How close counts as right.
 *
 * The table is quoted to one decimal and the sliders step in tenths, so anything
 * looser than this would call 38.1 a magic number, and anything tighter would fail
 * to recognise the table's own values through a float round-trip.
 */
export const TOL = 0.05;

export const isMagic = (key, value) => Math.abs(value - MAGIC[key]) <= TOL;

/** How far a length may be pushed. Wide enough that the linkage can be jammed. */
export const RANGE = KEYS.reduce((acc, key) => {
  const v = MAGIC[key];
  acc[key] = {
    min: Math.max(1, Math.round(v * 0.4 * 10) / 10),
    max: Math.round(v * 1.7 * 10) / 10,
    step: 0.1,
  };
  return acc;
}, {});

/**
 * The bars, in the order they are drawn.
 *
 * `a` and `l` are the frame offset rather than bars, so they are drawn as the same
 * dashed right-angle Jansen's own diagram uses. They still get a length each and can
 * still be moved, because moving the crank axle relative to the pivot wrecks the
 * gait just as thoroughly as moving a bar does.
 */
export const LINKS = [
  { key: 'a', from: 'A', to: 'G', frame: true },
  { key: 'l', from: 'G', to: 'B', frame: true },
  { key: 'm', from: 'B', to: 'P', crank: true },
  { key: 'b', from: 'A', to: 'T' },
  { key: 'j', from: 'P', to: 'T' },
  { key: 'd', from: 'A', to: 'L' },
  { key: 'e', from: 'T', to: 'L' },
  { key: 'c', from: 'A', to: 'M' },
  { key: 'k', from: 'P', to: 'M' },
  { key: 'f', from: 'L', to: 'K' },
  { key: 'g', from: 'M', to: 'K' },
  { key: 'h', from: 'K', to: 'F' },
  { key: 'i', from: 'M', to: 'F' },
];

/**
 * The two circles that fix each joint, and which of their two crossings to take.
 *
 * The branch signs are not free. Five binary choices give thirty-two assemblies of
 * the same eleven bars, and thirty-one of them are some other machine — most of them
 * fold the leg into a knot that travels a couple of dozen units and never touches
 * down. This one is the assembly with the 68-unit flat stroke.
 */
const STEPS = [
  { node: 'T', p: 'A', pr: 'b', q: 'P', qr: 'j', branch: -1 },
  { node: 'L', p: 'A', pr: 'd', q: 'T', qr: 'e', branch: -1 },
  { node: 'M', p: 'A', pr: 'c', q: 'P', qr: 'k', branch: +1 },
  { node: 'K', p: 'L', pr: 'f', q: 'M', qr: 'g', branch: +1 },
  { node: 'F', p: 'M', pr: 'i', q: 'K', qr: 'h', branch: -1 },
];

/**
 * Where two circles cross, or null where they cannot.
 *
 * That null is the whole collapse mechanism. A pair of bars too short to span the
 * gap between their anchors — or one so long the other cannot reach around it — is
 * a leg that physically will not assemble at this crank angle, and there is nothing
 * sensible to draw for the joint. The caller reports the bars that failed and stops.
 */
export const circleIntersect = (p, r1, q, r2, branch) => {
  const dx = q[0] - p[0];
  const dy = q[1] - p[1];
  const d = Math.hypot(dx, dy);
  if (d === 0 || d > r1 + r2 || d < Math.abs(r1 - r2)) return null;
  const t = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const hsq = r1 * r1 - t * t;
  if (hsq < 0) return null;
  const hh = Math.sqrt(hsq);
  const mx = p[0] + (t * dx) / d;
  const my = p[1] + (t * dy) / d;
  return [mx + (branch * hh * dy) / d, my - (branch * hh * dx) / d];
};

/**
 * Solve one leg at one crank angle.
 *
 * Returns every joint it managed to place, and — if it ran out — the joint it could
 * not place and the two bars responsible. Partial results are deliberate: a jammed
 * leg still draws everything up to the break, which is how you see *where* it broke.
 */
export const solveLeg = (r, theta) => {
  const nodes = {
    A: [0, 0],
    G: [r.a, 0],
    B: [r.a, r.l],
    P: [r.a + r.m * Math.cos(theta), r.l + r.m * Math.sin(theta)],
  };

  for (let s = 0; s < STEPS.length; s += 1) {
    const step = STEPS[s];
    const point = circleIntersect(
      nodes[step.p],
      r[step.pr],
      nodes[step.q],
      r[step.qr],
      step.branch
    );
    if (!point) {
      return {
        nodes,
        failed: {
          node: step.node,
          links: [step.pr, step.qr],
          // The two ends the missing joint was to be measured from. Carried out with
          // the failure so the drawing can show the two arcs that no longer meet.
          anchors: [nodes[step.p], nodes[step.q]],
        },
      };
    }
    nodes[step.node] = point;
  }

  return { nodes, failed: null };
};

/**
 * The path a joint traces over one full turn of the crank, in the leg's own frame.
 *
 * Entries are null wherever the leg will not assemble, so a broken linkage draws a
 * broken trail rather than a straight line across the gap it could not solve.
 */
export const jointPath = (r, node, samples = 240) => {
  const out = [];
  for (let i = 0; i <= samples; i += 1) {
    const { nodes, failed } = solveLeg(r, (i / samples) * Math.PI * 2);
    out.push(failed && !nodes[node] ? null : nodes[node]);
  }
  return out;
};

/** Shorthand for the one path that matters. */
export const footPath = (r, samples) => jointPath(r, 'F', samples);

/**
 * A leg's reach: how far below its pivot the foot gets, and how long the stroke is.
 *
 * Used for the readout rather than for the machine — the critter's ride height comes
 * from whatever is actually lowest on the current frame, not from a precomputed
 * number, so that a leg mid-collapse still rests on the ground properly.
 */
export const gait = (r, samples = 180) => {
  const path = footPath(r, samples).filter(Boolean);
  if (!path.length) return { drop: 0, stroke: 0, lift: 0, solvable: 0 };
  const ys = path.map((p) => p[1]);
  const xs = path.map((p) => p[0]);
  const low = Math.min(...ys);
  // The stroke is the span of the part of the cycle spent on the ground, which is
  // not the same as the full width of the loop: the swing reaches further back.
  const stance = path.filter((p) => p[1] < low + 1.5).map((p) => p[0]);
  return {
    drop: -low,
    stroke: stance.length ? Math.max(...stance) - Math.min(...stance) : 0,
    lift: Math.max(...ys) - low,
    width: Math.max(...xs) - Math.min(...xs),
    solvable: path.length / (samples + 1),
  };
};

/**
 * How a rigid frame with these contact points comes to rest on flat ground.
 *
 * This is the part that decides whether a bad set of numbers *looks* bad. Left to
 * itself a linkage always draws something plausible: the joints still close, the legs
 * still swing, and if the ground is simply painted under whatever is lowest then a
 * machine that would fall over in a heap still reads as marching. It does not fall
 * over because nothing is holding it up.
 *
 * So nothing holds it up here either. The frame rests on the lower convex hull of its
 * own joints, tilted onto whichever hull edge its weight actually sits over, and the
 * ground stays where it is. On Jansen's numbers every stance foot is at the same
 * height, the supporting edge is level, and the machine walks flat. On anything else
 * the feet land at different depths, the frame takes the angle of whatever pair it
 * happens to be standing on, and it lurches — which is what it would really do.
 *
 * Points are in the frame's own coordinates; `com` is where its weight acts. Returns
 * the angle to rotate the frame by, how far to lift it, and which points end up
 * carrying it.
 */
export const restOn = (points, comX = 0, maxTilt = 0.42) => {
  if (!points.length) return { pitch: 0, heave: 0, contacts: [], standing: false };

  // Lower convex hull, left to right — the only points that can ever touch.
  const sorted = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const hull = [];
  sorted.forEach((pt) => {
    while (hull.length >= 2) {
      const [ax, ay] = hull[hull.length - 2];
      const [bx, by] = hull[hull.length - 1];
      // Keep only right turns: anything above the chain cannot reach the ground.
      if ((bx - ax) * (pt[1] - ay) - (by - ay) * (pt[0] - ax) < 0) hull.pop();
      else break;
    }
    hull.push(pt);
  });

  const rot = (p, phi) => {
    const c = Math.cos(phi);
    const s = Math.sin(phi);
    return [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
  };

  const settleAt = (phi, standing) => {
    const turned = points.map((p) => rot(p, phi));
    const low = Math.min(...turned.map((p) => p[1]));
    const contacts = [];
    turned.forEach((p, i) => {
      // A hair of tolerance: feet that are level in theory land a thousandth apart.
      if (p[1] < low + 0.35) contacts.push(i);
    });
    return { pitch: phi, heave: -low, contacts, standing };
  };

  /*
   * The frame lies on the hull edge its weight sits over. More than one edge can
   * satisfy that — a convex shape has several poses it will sit in — so take the
   * flattest, which is the one the machine is already nearest to. Taking the first
   * instead has it standing on its nose because some edge back there is also a
   * technically valid way to rest.
   */
  let best = null;
  for (let i = 0; i < hull.length - 1; i += 1) {
    const p = hull[i];
    const q = hull[i + 1];
    const phi = -Math.atan2(q[1] - p[1], q[0] - p[0]);
    if (Math.abs(phi) > maxTilt) continue;
    const a = rot(p, phi)[0];
    const b = rot(q, phi)[0];
    const c = rot([comX, 0], phi)[0];
    if (c < Math.min(a, b) || c > Math.max(a, b)) continue;
    if (best === null || Math.abs(phi) < Math.abs(best)) best = phi;
  }
  if (best !== null) return settleAt(best, true);

  /*
   * Nothing under the weight: the frame is standing on one point, past it, and is on
   * its way over. Tipping is left proportional to the overhang and capped rather than
   * run to completion — a critter that has finished falling over is a still picture,
   * and the interesting state is the one where it is failing to stay up.
   */
  const pivot = hull.reduce((best, p) =>
    Math.abs(p[0] - comX) < Math.abs(best[0] - comX) ? p : best
  );
  const span = Math.max(1, hull[hull.length - 1][0] - hull[0][0]);
  const lean = Math.max(-1, Math.min(1, ((comX - pivot[0]) / span) * 2));
  return settleAt(-lean * maxTilt, false);
};

/** Green when a bar is Jansen's, red when it is not, warming as it drifts. */
export const RIGHT = '#8ac44f';
export const BROKEN = '#e8604c';

export const linkColour = (key, value) => {
  if (isMagic(key, value)) return RIGHT;
  /*
   * Off is off. The first tenth of a unit away from Jansen's number jumps most of the
   * way to red rather than shading gently out of green, because the reading that
   * matters is binary — a length either is one of the thirteen or it is not, and a
   * bar sitting at a yellowish green invites you to think it is near enough.
   *
   * The remaining travel, saturating within a few per cent, is only so that a bar
   * being dragged further and further wrong keeps saying so.
   */
  const off = Math.min(1, 0.55 + Math.abs(value - MAGIC[key]) / (MAGIC[key] * 0.05));
  const mix = (a, b) => Math.round(a + (b - a) * off);
  return `rgb(${mix(0x8a, 0xe8)}, ${mix(0xc4, 0x60)}, ${mix(0x4f, 0x4c)})`;
};
